import React, { useCallback, useEffect, useState } from "react";
import { Link, useLocation } from "react-router-dom";
import { ArrowDown, ArrowLeft, ArrowUp, ExternalLink, Plus, Save, Trash2 } from "lucide-react";
import { toast } from "sonner";
import { adminApi } from "../../lib/api";
import { pageHref } from "../../lib/format";
import { AdminHeader, Field, Panel, borderStyle, inputCls } from "../ui";
import MediaPicker from "../MediaPicker";
import SmartImage from "../../components/SmartImage";
import { PageLoader } from "../../components/States";

const BLOCK_TYPES = ["heading", "paragraph", "list", "image", "button", "quote"];

export default function PageEditor() {
  const location = useLocation();
  const slug = decodeURIComponent(location.pathname.replace(/^\/admin\/pages\/edit\/?/, ""));

  const [page, setPage] = useState(null);
  const [blocks, setBlocks] = useState([]);
  const [busy, setBusy] = useState(false);
  const [picker, setPicker] = useState(null);

  const load = useCallback(() => {
    adminApi
      .get(`/admin/pages/${slug}`)
      .then((r) => {
        setPage(r.data.page);
        setBlocks(r.data.page.blocks || []);
      })
      .catch(() => toast.error("Could not load page"));
  }, [slug]);

  useEffect(() => {
    load();
  }, [load]);

  if (!page) return <PageLoader />;

  const setField = (k) => (e) => setPage((p) => ({ ...p, [k]: e.target.value }));

  const updateBlock = (i, patch) =>
    setBlocks((bs) => bs.map((b, j) => (j === i ? { ...b, ...patch } : b)));

  const move = (i, dir) =>
    setBlocks((bs) => {
      const j = i + dir;
      if (j < 0 || j >= bs.length) return bs;
      const next = [...bs];
      [next[i], next[j]] = [next[j], next[i]];
      return next;
    });

  const addBlock = (type) => {
    const base = { type };
    if (type === "heading") Object.assign(base, { level: 2, text: "New heading" });
    if (type === "paragraph" || type === "quote") base.text = "";
    if (type === "list") base.items = [""];
    if (type === "image") Object.assign(base, { url: "", alt: "" });
    if (type === "button") Object.assign(base, { text: "Enquire now", href: "/enquire", slug: "" });
    setBlocks((bs) => [...bs, base]);
  };

  async function save() {
    setBusy(true);
    try {
      await adminApi.put(`/admin/pages/${slug}`, {
        title: page.title,
        h1: page.h1,
        seo_title: page.seo_title,
        meta_description: page.meta_description,
        hero_image: page.hero_image,
        blocks,
        published: page.published,
        is_hub: page.is_hub,
      });
      toast.success("Page saved");
      load();
    } catch (err) {
      toast.error("Save failed", { description: err?.response?.data?.detail });
    } finally {
      setBusy(false);
    }
  }

  return (
    <div data-testid="admin-page-editor">
      <AdminHeader title={page.title} subtitle={`/${page.slug}`}>
        <Link to="/admin/pages" className="btn-outline-nq">
          <ArrowLeft className="h-4 w-4" /> Back
        </Link>
        <a href={pageHref(page.slug)} target="_blank" rel="noreferrer" className="btn-outline-nq">
          <ExternalLink className="h-4 w-4" /> View
        </a>
        <button type="button" onClick={save} disabled={busy} className="btn-gold" data-testid="page-editor-save">
          <Save className="h-4 w-4" /> {busy ? "Saving..." : "Save changes"}
        </button>
      </AdminHeader>

      <div className="grid gap-6 lg:grid-cols-12">
        <div className="space-y-4 lg:col-span-8">
          <Panel>
            <h2 className="mb-4 text-sm font-semibold uppercase tracking-wider gold-text">Content blocks</h2>
            <div className="space-y-3">
              {blocks.map((b, i) => (
                <div
                  key={i}
                  className="rounded-lg border p-3"
                  style={{ borderColor: "rgba(255,255,255,0.1)" }}
                  data-testid="editor-block"
                >
                  <div className="mb-2 flex items-center gap-2">
                    <span className="rounded px-2 py-0.5 text-[10px] font-bold uppercase tracking-wider" style={{ background: "rgba(198,162,74,0.18)", color: "var(--nq-gold)" }}>
                      {b.type}
                    </span>
                    <div className="ml-auto flex items-center gap-1">
                      <button type="button" onClick={() => move(i, -1)} className="rounded p-1 hover:bg-white/10" title="Move up">
                        <ArrowUp className="h-3.5 w-3.5" />
                      </button>
                      <button type="button" onClick={() => move(i, 1)} className="rounded p-1 hover:bg-white/10" title="Move down">
                        <ArrowDown className="h-3.5 w-3.5" />
                      </button>
                      <button
                        type="button"
                        onClick={() => setBlocks((bs) => bs.filter((_, j) => j !== i))}
                        className="rounded p-1 hover:bg-white/10"
                        title="Delete block"
                        data-testid="editor-block-delete"
                      >
                        <Trash2 className="h-3.5 w-3.5" style={{ color: "#e07a72" }} />
                      </button>
                    </div>
                  </div>

                  {b.type === "heading" && (
                    <div className="flex gap-2">
                      <select
                        value={b.level || 2}
                        onChange={(e) => updateBlock(i, { level: Number(e.target.value) })}
                        className="rounded-lg border bg-transparent px-2 text-sm"
                        style={{ ...borderStyle, background: "var(--nq-ink)" }}
                      >
                        {[1, 2, 3, 4, 5, 6].map((l) => (
                          <option key={l} value={l}>H{l}</option>
                        ))}
                      </select>
                      <input
                        value={b.text || ""}
                        onChange={(e) => updateBlock(i, { text: e.target.value })}
                        className={inputCls}
                        style={borderStyle}
                        data-testid="editor-heading-input"
                      />
                    </div>
                  )}

                  {(b.type === "paragraph" || b.type === "quote") && (
                    <textarea
                      rows={4}
                      value={b.text || ""}
                      onChange={(e) => updateBlock(i, { text: e.target.value })}
                      className={inputCls}
                      style={borderStyle}
                      data-testid="editor-paragraph-input"
                    />
                  )}

                  {b.type === "list" && (
                    <textarea
                      rows={4}
                      value={(b.items || []).join("\n")}
                      onChange={(e) => updateBlock(i, { items: e.target.value.split("\n") })}
                      placeholder="One item per line"
                      className={inputCls}
                      style={borderStyle}
                    />
                  )}

                  {b.type === "image" && (
                    <div className="flex items-center gap-3">
                      <div className="w-24 shrink-0">
                        <SmartImage src={b.url} alt={b.alt} ratio="aspect-square" fit="contain" />
                      </div>
                      <div className="flex-1 space-y-2">
                        <input
                          value={b.alt || ""}
                          onChange={(e) => updateBlock(i, { alt: e.target.value })}
                          placeholder="Alt text"
                          className={inputCls}
                          style={borderStyle}
                        />
                        <button
                          type="button"
                          onClick={() => setPicker({ onPick: (m) => updateBlock(i, { url: m.url }) })}
                          className="btn-outline-nq px-3 py-1.5 text-xs"
                        >
                          Choose image
                        </button>
                      </div>
                    </div>
                  )}

                  {b.type === "button" && (
                    <div className="grid gap-2 sm:grid-cols-2">
                      <input
                        value={b.text || ""}
                        onChange={(e) => updateBlock(i, { text: e.target.value })}
                        placeholder="Label"
                        className={inputCls}
                        style={borderStyle}
                      />
                      <input
                        value={b.href || ""}
                        onChange={(e) => updateBlock(i, { href: e.target.value, slug: "" })}
                        placeholder="/enquire"
                        className={inputCls}
                        style={borderStyle}
                      />
                    </div>
                  )}
                </div>
              ))}
            </div>

            <div className="mt-4 flex flex-wrap gap-2 border-t pt-4" style={{ borderColor: "rgba(255,255,255,0.08)" }}>
              {BLOCK_TYPES.map((t) => (
                <button
                  key={t}
                  type="button"
                  onClick={() => addBlock(t)}
                  className="btn-outline-nq px-3 py-1.5 text-xs"
                  data-testid={`add-block-${t}`}
                >
                  <Plus className="h-3 w-3" /> {t}
                </button>
              ))}
            </div>
          </Panel>
        </div>

        <div className="space-y-4 lg:col-span-4">
          <Panel>
            <h2 className="mb-4 text-sm font-semibold uppercase tracking-wider gold-text">Page details</h2>
            <div className="space-y-3">
              <Field label="Title">
                <input value={page.title || ""} onChange={setField("title")} className={inputCls} style={borderStyle} data-testid="editor-title-input" />
              </Field>
              <Field label="H1 heading">
                <input value={page.h1 || ""} onChange={setField("h1")} className={inputCls} style={borderStyle} />
              </Field>
              <Field label="SEO title">
                <input value={page.seo_title || ""} onChange={setField("seo_title")} className={inputCls} style={borderStyle} />
              </Field>
              <Field label="Meta description">
                <textarea rows={3} value={page.meta_description || ""} onChange={setField("meta_description")} className={inputCls} style={borderStyle} />
              </Field>
              <label className="flex items-center gap-2 text-sm">
                <input
                  type="checkbox"
                  checked={!!page.published}
                  onChange={(e) => setPage((p) => ({ ...p, published: e.target.checked }))}
                  className="accent-[color:var(--nq-gold)]"
                  data-testid="editor-published-checkbox"
                />
                Published
              </label>
            </div>
          </Panel>

          <Panel>
            <h2 className="mb-3 text-sm font-semibold uppercase tracking-wider gold-text">Hero image</h2>
            <SmartImage src={page.hero_image} alt="" ratio="aspect-[16/10]" fit="contain" />
            <button
              type="button"
              onClick={() => setPicker({ onPick: (m) => setPage((p) => ({ ...p, hero_image: m.url })) })}
              className="btn-outline-nq mt-3 w-full px-3 py-2 text-xs"
              data-testid="editor-hero-picker"
            >
              Change hero image
            </button>
          </Panel>
        </div>
      </div>

      {picker && (
        <MediaPicker
          onClose={() => setPicker(null)}
          onPick={(m) => {
            picker.onPick(m);
            setPicker(null);
          }}
        />
      )}
    </div>
  );
}
